import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { useApp } from '@/hooks/use-app-context';
import { useLanguages } from '@/hooks/use-languages';
import { cleanLanguageName } from '@/lib/language-name';
import { ExportButton } from './filters/ExportButton';
import { LeaderboardFilters } from './filters/LeaderboardFilters';
import { LeaderboardEmpty } from './empty/LeaderboardEmpty';
import { LeaderboardError } from './error/LeaderboardError';
import { useLeaderboard } from './hooks/useLeaderboard';
import { fill, leaderboardCopy, type LeaderboardCopy } from './leaderboard-copy';
import { filterEntries, findCurrentEntry, gapToNextRank, isCurrentUser, splitPodium } from './leaderboard-ranking';
import { ALL_LANGUAGES, type LeaderboardEntry, type LeaderboardPeriod } from './leaderboard.types';
import { LeaderboardPodium } from './podium/LeaderboardPodium';
import { YourPosition } from './position/YourPosition';
import { LeaderboardSkeleton } from './skeleton/LeaderboardSkeleton';
import { LeaderboardTable } from './table/LeaderboardTable';

/** Staff accounts are left out of the board by the backend: no position to show. */
const STAFF_ROLES = new Set(['admin', 'super_admin', 'accountant', 'COMPTABLE']);

/**
 * Public leaderboard (visitors too): who contributed most, per language and
 * period. A signed-in contributor sees their own position first.
 */
export default function LeaderboardPage() {
  const { lang = 'fr' } = useParams<{ lang: string }>();
  const { t, user } = useApp();
  const { activeLanguages, languages: allLanguages } = useLanguages();
  // Codes from the API, names from the language list; the code if unknown.
  const nameOfLanguage = (code: string) =>
    cleanLanguageName(code, allLanguages.find((l) => l.code?.toLowerCase() === code.toLowerCase())?.displayName ?? code, lang) || code;
  const filterLanguages = useMemo(
    () =>
      activeLanguages
        .map((l) => ({ ...l, displayName: cleanLanguageName(l.code, l.displayName, lang) }))
        .filter((l) => l.displayName),
    [activeLanguages, lang],
  );
  const copy: LeaderboardCopy = leaderboardCopy[lang === 'en' ? 'en' : 'fr'];
  const locale = lang === 'en' ? 'en-GB' : 'fr-FR';
  const format = useMemo(() => {
    const formatter = new Intl.NumberFormat(locale);
    return (value: number) => formatter.format(value);
  }, [locale]);

  const [language, setLanguage] = useState<string>(ALL_LANGUAGES);
  const [period, setPeriod] = useState<LeaderboardPeriod>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const viewMode = language === ALL_LANGUAGES ? 'global' : 'language';
  const effectiveLanguage = viewMode === 'language' ? language : undefined;
  const { data, isLoading, isError, refetch } = useLeaderboard(viewMode, 'contributions', effectiveLanguage, period);

  const entries = useMemo(() => data?.entries ?? [], [data]);
  const total = data?.total ?? entries.length;
  const searching = search.trim().length > 0;
  const { podium, rest } = splitPodium(entries);
  const listed = searching ? filterEntries(entries, search) : rest;

  const mine = findCurrentEntry(entries, user);
  const isMine = (entry: LeaderboardEntry) => isCurrentUser(entry, user);
  const showPosition = Boolean(user) && !STAFF_ROLES.has(String(user?.role ?? '')) && entries.length > 0;

  const languageName = nameOfLanguage(language);
  const scope = viewMode === 'global' ? copy.scopeAll : fill(copy.scopeLanguage, { language: languageName });
  const periodText = period === 'week' ? copy.periodWeekShort : period === 'month' ? copy.periodMonthShort : copy.periodAllShort;

  const resetPage = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setPage(1);
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
      <PageHeader
        title={String(t.leaderboard ?? '')}
        description={
          <>
            {entries.length > 0 ? fill(total === 1 ? copy.descriptionCountOne : copy.descriptionCount, { count: format(total), scope, period: periodText }) : copy.description}
            <span className="mt-1 block text-xs text-faint-foreground">{copy.counted}</span>
          </>
        }
        actions={entries.length > 0 ? <ExportButton copy={copy} viewMode={viewMode} language={effectiveLanguage} /> : null}
      >
        <LeaderboardFilters
          copy={copy}
          languages={filterLanguages}
          language={language}
          onLanguageChange={resetPage(setLanguage)}
          period={period}
          onPeriodChange={resetPage(setPeriod)}
          search={search}
          onSearchChange={resetPage(setSearch)}
        />
      </PageHeader>

      {isLoading ? (
        <LeaderboardSkeleton />
      ) : isError ? (
        <LeaderboardError copy={copy} retryLabel={t.leaderboardRetry} onRetry={() => void refetch()} />
      ) : entries.length === 0 ? (
        <LeaderboardEmpty copy={copy} />
      ) : (
        <>
          {showPosition ? (
            <YourPosition copy={copy} base={`/${lang}`} entry={mine} gap={gapToNextRank(entries, mine)} total={entries.length} format={format} />
          ) : null}

          {!searching ? <LeaderboardPodium copy={copy} entries={podium} isMine={isMine} format={format} languageName={nameOfLanguage} /> : null}

          {searching && listed.length === 0 ? (
            <div className="rounded-lg border border-border/60 bg-surface">
              <EmptyState
                compact
                icon="search"
                title={fill(copy.noResults, { query: search.trim() })}
                action={
                  <Button variant="outline" size="sm" onClick={() => resetPage(setSearch)('')}>
                    {copy.clearSearch}
                  </Button>
                }
              />
            </div>
          ) : listed.length > 0 ? (
            <LeaderboardTable
              languageName={nameOfLanguage}
              copy={copy}
              entries={listed}
              isMine={isMine}
              format={format}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={resetPage(setPageSize)}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
